import React, { useEffect, useState } from "react";
import {
  calculatePresentValue,
  calculateFutureValue,
  calculatePeriodicPayment,
  rmdDistributionPeriods,
  stateIncomeTaxRates,
  calculateFederalIncomeTax,
  allocateSavings,
} from "./retirementGoalCalculations";
import { formatCurrencyAmount } from "./formatAmount";

export default function RetirementProjection({ payload = {}, taxInfo }) {
  const [yearsUntilRetirement, setYearsUntilRetirement] = useState(0);
  const [yearsInRetirement, setYearsInRetirement] = useState(0);
  const [totalAnnualIncome, setTotalAnnualIncome] = useState(0);
  const [totalRetirementIncome, setTotalRetirementIncome] = useState(0);
  const [inflationAdjustedGoal, setInflationAdjustedGoal] = useState(0);
  const [federalTaxEstimate, setFederalTaxEstimate] = useState(0);
  const [stateTaxEstimate, setStateTaxEstimate] = useState(0);
  const [netAfterTax, setNetAfterTax] = useState(0);
  const [savingsAllocationMonthly, setSavingsAllocationMonthly] = useState({
    deferred: 0,
    free: 0,
    taxable: 0,
  });
  const [projectedValueCurrentSavings, setProjectedValueCurrentSavings] =
    useState(0);
  const [totalSavingsGoal, setTotalSavingsGoal] = useState(0);
  const [finalSavingsShortfall, setFinalSavingsShortfall] = useState(0);
  const [
    requiredAdditionalMonthlySavings,
    setRequiredAdditionalMonthlySavings,
  ] = useState(0);
  const [projectionTable, setProjectionTable] = useState([]);
  const [taxableAccount, setTaxableAccount] = useState([]);
  const [deferredAccount, setDeferredAccount] = useState([]);
  const [rothAccount, setRothAccount] = useState([]);
  const [withdrawalSummary, setWithdrawalSummary] = useState([]);
  const [proposedMonthlySavings, setProposedMonthlySavings] = useState(null);

  const getNum = (key, fallback = 0) => {
    const v = payload && payload[key];
    if (v === undefined || v === null || v === "") return fallback;
    return typeof v === "number"
      ? v
      : parseFloat(String(v).replace(/[^0-9.\-]/g, "")) || fallback;
  };

  useEffect(() => {
    if (!payload) return;

    const currentAge = getNum("Current Age");
    const desiredRetAge = getNum("Desired Retirement Age");
    const planUntilAge = getNum("Plan Until Age (Life Expectancy)");

    const inflationInput = payload["Inflation rate for this goal"];
    const inflationRate =
      inflationInput === "General" || inflationInput === undefined
        ? 0.05
        : (() => {
            const num = parseFloat(
              String(inflationInput).replace(/[^0-9.\-]/g, "")
            );
            return num > 1 ? num / 100 : num || 0;
          })();

    const annualReturnRate = getNum("annualReturnRate", 0.05);
    const annualPostRetBudget = getNum("Budget Post-Retirement");

    const ssUser = getNum("Your Estimated Social Security");
    const ssSpouse = getNum("Social Security - Spouse");
    const pension = getNum("Pension");
    const rental = getNum("Net Rental Income");

    const annualIncomes = Array.isArray(payload["Annual Amount"])
      ? payload["Annual Amount"]
      : payload["Annual Amount"]
      ? [payload["Annual Amount"]]
      : [];
    const sumAdditionalIncome = annualIncomes.reduce(
      (s, v) => s + (parseFloat(v) || 0),
      0
    );

    const taxableBalance = getNum(
      "taxableBalance",
      getNum("Taxable Balance", 100) // Input Field Taxable Accounts Balance (Brokerage, Savings)
    );
    const taxDeferredBalance = getNum(
      "taxDeferredBalance",
      getNum("Tax-Deferred Balance", 100) // Input Field Tax-Deferred Accounts Balance (Traditional 401k/IRA)
    );
    const taxFreeBalance = getNum(
      "taxFreeBalance",
      getNum("Tax-Free Balance", 200) // Input Field Tax-Free Accounts Balance (Roth 401k/IRA)
    );

    const taxableContrib = getNum("taxableContrib", 0); // Input Field Current Taxable Accounts Annual Contribution
    const taxDeferredContrib = getNum("taxDeferredContrib", 100); // Input Field Current Tax-Deferred Accounts Annual Contribution
    const taxFreeContrib = getNum("taxFreeContrib", 100); // Input Field Current Tax-Free Accounts Annual Contribution

    const userProposedMonthlyFromPayload = getNum(
      "userProposedMonthlySavings",
      NaN
    );

    const yearsToRetirement = Math.max(0, desiredRetAge - currentAge);
    const yearsInRetirement = Math.max(0, planUntilAge - desiredRetAge);

    const totalAnnualIncomeVal = sumAdditionalIncome + getNum("Annual Salary");
    const totalRetirementIncomeVal =
      ssUser + ssSpouse + pension + rental + sumAdditionalIncome;

    const inflationAdjustedGoalVal = -calculateFutureValue(
      inflationRate,
      yearsToRetirement,
      0,
      -1
    );

    // const federalTaxVal = calculateFederalIncomeTax(totalRetirementIncomeVal);

    // derive filing status and spouse age (fallback to payload fields if provided)
    const filingStatus = payload && payload["Tax Filing Status"] || "Single";

    const spouseAge = getNum("Spouse's Current Age", 0);

    // pass age info so standard deduction is calculated properly
    const federalTaxVal = calculateFederalIncomeTax(totalRetirementIncomeVal, {
      filingStatus,
      age: currentAge,
      spouseAge,
    });

    const stateName =
      payload["State of Residence in Retirement"] || payload["state"] || "";
    const stateTaxRate =
      (stateIncomeTaxRates && stateIncomeTaxRates[stateName]) || 0;
    const stateTaxVal = totalRetirementIncomeVal * stateTaxRate;
    const netAfterTaxVal =
      totalRetirementIncomeVal - federalTaxVal - stateTaxVal;

    const currentTotalContrib =
      taxableContrib + taxDeferredContrib + taxFreeContrib;
    const currentTotalBalance =
      taxableBalance + taxDeferredBalance + taxFreeBalance;

    const projectedValue = -calculateFutureValue(
      annualReturnRate,
      yearsToRetirement,
      -currentTotalContrib,
      -currentTotalBalance
    );

    const realRateOfReturn =
      (annualReturnRate - inflationRate) / (1 + inflationRate);
    const totalSavingsGoalAtRetirementInTodaysDollars = -calculatePresentValue(
      realRateOfReturn,
      yearsInRetirement,
      -annualPostRetBudget,
      0
    );
    const totalSavingsGoalNominal =
      totalSavingsGoalAtRetirementInTodaysDollars *
      Math.pow(1 + inflationRate, yearsToRetirement);

    const finalShortfall = Math.max(
      0,
      totalSavingsGoalNominal - projectedValue
    );

    const requiredMonthly =
      finalShortfall > 0
        ? Math.max(
            0,
            -calculatePeriodicPayment(
              annualReturnRate / 12,
              Math.max(1, yearsToRetirement * 12),
              0,
              finalShortfall
            )
          )
        : 0;

    const proposedMonthly = Number.isFinite(userProposedMonthlyFromPayload)
      ? userProposedMonthlyFromPayload
      : requiredMonthly;
    const allocationMonthly = allocateSavings(proposedMonthly);

    setYearsUntilRetirement(yearsToRetirement);
    setYearsInRetirement(yearsInRetirement);
    setTotalAnnualIncome(totalAnnualIncomeVal);
    setTotalRetirementIncome(totalRetirementIncomeVal);
    setInflationAdjustedGoal(inflationAdjustedGoalVal);
    setFederalTaxEstimate(federalTaxVal);
    setStateTaxEstimate(stateTaxVal);
    setNetAfterTax(netAfterTaxVal);

    setProjectedValueCurrentSavings(projectedValue);
    setTotalSavingsGoal(totalSavingsGoalNominal);
    setFinalSavingsShortfall(finalShortfall);
    setRequiredAdditionalMonthlySavings(requiredMonthly);

    setSavingsAllocationMonthly(allocationMonthly);
    setProposedMonthlySavings(proposedMonthly);

    function runFullProjection(inputs) {
      const {
        yearsToRetirement,
        annualReturnRate,
        taxableBalance,
        taxDeferredBalance,
        taxFreeBalance,
        taxableContrib,
        taxDeferredContrib,
        taxFreeContrib,
        yearsInRetirement,
        annualPostRetBudget,
        inflationRate,
        desiredRetAge,
        state,
      } = inputs;

      let taxableStartBal = -calculateFutureValue(
        annualReturnRate,
        yearsToRetirement,
        -taxableContrib,
        -taxableBalance
      );
      let taxDeferredStartBal = -calculateFutureValue(
        annualReturnRate,
        yearsToRetirement,
        -taxDeferredContrib,
        -taxDeferredBalance
      );
      let taxFreeStartBal = -calculateFutureValue(
        annualReturnRate,
        yearsToRetirement,
        -taxFreeContrib,
        -taxFreeBalance
      );

      const rows = [];

      for (let y = 0; y < yearsInRetirement; y++) {
        const year = new Date().getFullYear() + yearsToRetirement + y;
        const age = desiredRetAge + y;

        const spendingNeed =
          annualPostRetBudget *
          Math.pow(1 + inflationRate, y + yearsToRetirement);

        const fixedIncomeTotal =
          (ssUser + ssSpouse + pension + rental + sumAdditionalIncome) *
          Math.pow(1 + inflationRate, y + yearsToRetirement);

        let portfolioWithdrawalNeed = Math.max(
          0,
          spendingNeed - fixedIncomeTotal
        );

        const taxableStart = taxableStartBal;
        const taxDeferredStart = taxDeferredStartBal;
        const taxFreeStart = taxFreeStartBal;

        const rmdPeriod = rmdDistributionPeriods[age];
        // const rmdAmt =
        //   age >= 73 && rmdPeriod && taxDeferredStart > 0
        //     ? taxDeferredStart / rmdPeriod
        //     : 0;

        const rmdAmt =
          age >= 73 && rmdPeriod && rmdPeriod > 0 && taxDeferredStart > 0
            ? taxDeferredStart / rmdPeriod
            : 0;

        let taxableWithdraw = Math.min(portfolioWithdrawalNeed, taxableStart);
        portfolioWithdrawalNeed -= taxableWithdraw;

        // let taxDeferredWithdraw =
        //   rmdAmt > 0
        //     ? Math.max(
        //         rmdAmt,
        //         Math.min(portfolioWithdrawalNeed, taxDeferredStart)
        //       )
        //     : Math.min(portfolioWithdrawalNeed, taxDeferredStart);

        let taxDeferredWithdraw =
          rmdAmt > 0
            ? Math.min(
                Math.max(rmdAmt, 0),
                taxDeferredStart,
                portfolioWithdrawalNeed
              )
            : Math.min(portfolioWithdrawalNeed, taxDeferredStart);

        portfolioWithdrawalNeed -= taxDeferredWithdraw;

        let rothWithdraw = Math.min(portfolioWithdrawalNeed, taxFreeStart);
        portfolioWithdrawalNeed -= rothWithdraw;

        const taxableGrowth =
          (taxableStart - taxableWithdraw) * annualReturnRate;
        const taxDeferredGrowth =
          (taxDeferredStart - taxDeferredWithdraw) * annualReturnRate;
        const rothGrowth = (taxFreeStart - rothWithdraw) * annualReturnRate;

        // const taxableEnd = Math.max(
        //   0,
        //   taxableStart - taxableWithdraw + taxableGrowth
        // );
        const taxableEnd = Math.max(
          0,
          taxableStart - taxableWithdraw + taxableGrowth
        );
        // const taxDeferredEnd = Math.max(
        //   0,
        //   taxDeferredStart - taxDeferredWithdraw + taxDeferredGrowth
        // );
        const taxDeferredEnd = Math.max(
          0,
          taxDeferredStart - taxDeferredWithdraw + taxDeferredGrowth
        );
        const rothEnd = Math.max(0, taxFreeStart - rothWithdraw + rothGrowth);

        const totalWithdrawals =
          taxableWithdraw + taxDeferredWithdraw + rothWithdraw;
        // const totalPreTaxIncome = fixedIncomeTotal + taxDeferredWithdraw;
        const taxableIncomeThisYear = taxableWithdraw;
        const taxDeferredIncomeThisYear = taxDeferredWithdraw;

        const totalPreTaxIncome =
          fixedIncomeTotal + taxDeferredIncomeThisYear + taxableIncomeThisYear;

        const stateTaxes =
          totalPreTaxIncome * (stateIncomeTaxRates[state] || 0);
        // const federalTaxes = calculateFederalIncomeTax(totalPreTaxIncome);
        // for each projection row use the age for additional standard deduction if applicable
        const rowFilingStatus = payload && payload["Tax Filing Status"] || "Single";
        // if you have spouse age per-row, you can pass that too; otherwise we reuse spouseAge from payload
        const federalTaxes = calculateFederalIncomeTax(totalPreTaxIncome, {
          filingStatus: rowFilingStatus,
          age: parseInt(age), // age is defined in the loop as desiredRetAge + y
          spouseAge,
        });


        const totalEndingBalance = taxableEnd + taxDeferredEnd + rothEnd;

        rows.push({
          year,
          age,
          retirementStatus: "Retired",
          annualSpendingNeed: spendingNeed,
          blankCell: "",
          socialSecurityIncome:
            (ssUser + ssSpouse) *
            Math.pow(1 + inflationRate, y + yearsToRetirement),
          pensionIncome:
            pension * Math.pow(1 + inflationRate, y + yearsToRetirement),
          otherIncome:
            (rental + sumAdditionalIncome) *
            Math.pow(1 + inflationRate, y + yearsToRetirement),
          portfolioWithdrawalNeed,
          taxableStart,
          taxableGrowth,
          taxableWithdrawals: taxableWithdraw,
          taxableEnd,
          taxDeferredStart,
          taxDeferredGrowth,
          rmdCalculation: rmdAmt,
          taxDeferredWithdrawals: taxDeferredWithdraw,
          taxDeferredEnd,
          rothStart: taxFreeStart,
          rothGrowth,
          rothWithdrawals: rothWithdraw,
          rothEnd,
          totalWithdrawals,
          totalPreTaxIncome,
          estimatedStateTaxesPaid: stateTaxes,
          estimatedFederalTaxesPaid: federalTaxes,
          totalEndingBalance,
        });

        taxableStartBal = taxableEnd;
        taxDeferredStartBal = taxDeferredEnd;
        taxFreeStartBal = rothEnd;
      }

      return rows;
    }

    const inputsForProjection = {
      yearsToRetirement,
      annualReturnRate,
      taxableBalance,
      taxDeferredBalance,
      taxFreeBalance,
      taxableContrib,
      taxDeferredContrib,
      taxFreeContrib,
      yearsInRetirement,
      annualPostRetBudget,
      inflationRate,
      desiredRetAge,
      state: stateName,
    };

    const tableRows = runFullProjection(inputsForProjection);

    setProjectionTable(tableRows);
    setTaxableAccount(
      tableRows.map((r) => ({
        year: r.year,
        start: r.taxableStart,
        growth: r.taxableGrowth,
        withdrawal: r.taxableWithdrawals,
        end: r.taxableEnd,
      }))
    );
    setDeferredAccount(
      tableRows.map((r) => ({
        year: r.year,
        start: r.taxDeferredStart,
        growth: r.taxDeferredGrowth,
        rmd: r.rmdCalculation,
        withdrawal: r.taxDeferredWithdrawals,
        end: r.taxDeferredEnd,
      }))
    );
    setRothAccount(
      tableRows.map((r) => ({
        year: r.year,
        start: r.rothStart,
        growth: r.rothGrowth,
        withdrawal: r.rothWithdrawals,
        end: r.rothEnd,
      }))
    );
    setWithdrawalSummary(
      tableRows.map((r) => ({
        year: r.year,
        totalWithdrawals: r.totalWithdrawals,
        preTaxIncome: r.totalPreTaxIncome,
        stateTaxes: r.estimatedStateTaxesPaid,
        federalTaxes: r.estimatedFederalTaxesPaid,
      }))
    );
  }, [payload]);

  return (
    <div className="ret-projection p-4">
      <h2 className="text-2xl font-semibold mb-3">Retirement Plan Results</h2>

      <div className="summary grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
        <div className="summary-item">
          <label>Total Savings Goal at Retirement:</label>{" "}
          <strong>{formatCurrencyAmount(totalSavingsGoal)}</strong>
        </div>
        <div className="summary-item">
          <label>Projected Value (with current savings):</label>{" "}
          <strong>{formatCurrencyAmount(projectedValueCurrentSavings)}</strong>
        </div>
        <div className="summary-item highlight">
          <label>Required Additional Monthly Savings:</label>{" "}
          <strong>
            {formatCurrencyAmount(requiredAdditionalMonthlySavings)}
          </strong>
        </div>
        <div className="summary-item">
          <label>Your Proposed Monthly Savings:</label>{" "}
          <strong>
            {formatCurrencyAmount(
              proposedMonthlySavings || requiredAdditionalMonthlySavings
            )}
          </strong>
        </div>
        <div className="summary-item">
          <label>Suggested Allocation (monthly):</label>
          <div>
            Tax-Deferred:{" "}
            {formatCurrencyAmount(savingsAllocationMonthly.deferred)} / mo,
            Tax-Free: {formatCurrencyAmount(savingsAllocationMonthly.free)} /
            mo, Taxable:{" "}
            {formatCurrencyAmount(savingsAllocationMonthly.taxable)} / mo
          </div>
        </div>
        <div className="summary-item">
          <label>Plan Net After Tax (annual):</label>{" "}
          <strong>{formatCurrencyAmount(netAfterTax)}</strong>
        </div>
      </div>

      <hr style={{ margin: "20px 0" }} />

      <h3 className="text-lg font-semibold mb-3">
        Annual Projection (Based on Your Plan)
      </h3>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {[
                "Year",
                "Age",
                "Retirement Status",
                "Annual Spending Need (Inflated)",
                "",
                "Social Security Income",
                "Pension Income",
                "Other Income (Rental, etc.)",
                "Portfolio Withdrawal Need",
                "Taxable Acct: Start Balance",
                "Taxable Acct: Growth",
                "Taxable Acct: Withdrawals",
                "Taxable Acct: End Balance",
                "Tax-Deferred Acct: Start Balance",
                "Tax-Deferred Acct: Growth",
                "RMD Calculation",
                "Tax-Deferred Acct: Withdrawals",
                "Tax-Deferred Acct: End Balance",
                "Roth Acct: Start Balance",
                "Roth Acct: Growth",
                "Roth Acct: Withdrawals",
                "Roth Acct: End Balance",
                "Total Withdrawals From All Accounts",
                "Total Pre-Tax Income",
                "Estimated State Taxes Paid",
                "Estimated Federal Taxes Paid",
                "Total Ending Balance (All Accounts)",
              ].map((c) => (
                <th
                  key={c}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    background: "#f6f6f6",
                  }}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {projectionTable.map((r, i) => (
              <tr key={i} style={{ borderTop: "1px solid #eee" }}>
                <td style={{ padding: "6px", textAlign: "center" }}>
                  {r.year}
                </td>
                <td style={{ padding: "6px", textAlign: "center" }}>{r.age}</td>
                <td style={{ padding: "6px", textAlign: "center" }}>
                  {r.retirementStatus}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.annualSpendingNeed)}
                </td>
                <td style={{ padding: "6px" }}>&nbsp;</td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.socialSecurityIncome)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.pensionIncome)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.otherIncome)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.portfolioWithdrawalNeed)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.taxableStart)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.taxableGrowth)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.taxableWithdrawals)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.taxableEnd)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.taxDeferredStart)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.taxDeferredGrowth)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {r.rmdCalculation > 0
                    ? formatCurrencyAmount(r.rmdCalculation)
                    : "-"}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.taxDeferredWithdrawals)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.taxDeferredEnd)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.rothStart)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.rothGrowth)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.rothWithdrawals)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.rothEnd)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.totalWithdrawals)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.totalPreTaxIncome)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.estimatedStateTaxesPaid)}
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  {formatCurrencyAmount(r.estimatedFederalTaxesPaid)}
                </td>
                <td
                  style={{
                    padding: "6px",
                    textAlign: "right",
                    fontWeight: "bold",
                  }}
                >
                  {formatCurrencyAmount(r.totalEndingBalance)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
